import {DataTable, DataTableColumn} from "../DataTable/DataTable";
import {reportTimeType, SettlementEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {Anchor, Group} from "@mantine/core";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {usePagination} from "../DataTable/usePagination";
import {useSettlementDataTableFilter} from "./useSettlementDataTableFilter";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode"
import {Link} from "react-router-dom";
import {FormattedActivityPeriodDate} from "../../date/FormattedActivityPeriodDate/FormattedActivityPeriodDate";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {useMemo} from "react";
import {sortBy} from "lodash";

const compareDate = (a: SettlementEntity, b: SettlementEntity): number => {
  let aDttm: number | undefined = undefined;
  let bDttm: number | undefined = undefined;

  if (a.activityPeriodType === reportTimeType.daily) {
    aDttm = a.activityPeriodFromDttm
  } else if (a.activityPeriodType === reportTimeType.weekly) {
    aDttm = a.activityPeriodToDttm
  } else if (a.activityPeriodType === reportTimeType.monthly) {
    aDttm = a.activityPeriodToDttm
  }

  if (b.activityPeriodType === reportTimeType.daily) {
    bDttm = b.activityPeriodFromDttm
  } else if (b.activityPeriodType === reportTimeType.weekly) {
    bDttm = b.activityPeriodToDttm
  } else if (b.activityPeriodType === reportTimeType.monthly) {
    bDttm = b.activityPeriodToDttm
  }

  if (aDttm) {
    if (bDttm === undefined || aDttm > bDttm) {
      return 1;
    }
  }

  return -1;
};

const EMPTY_ARRAY: SettlementEntity[] = []

export type SettlementDataTableProps = EntityDataTableProps<SettlementEntity>

export const SettlementDataTable = (props: SettlementDataTableProps) => {
  const {
    records: settlements = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props;

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useSettlementDataTableFilter({
    records: settlements
  })

  const sortConfig = useMemo(() => {
    return {
      "activityPeriodFromDttm": {
        sort: compareDate,
      }
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'fromDttm', direction: 'desc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns : DataTableColumn<SettlementEntity>[] = useMemo(() => {
    return [
      {
        ...getColumnFilterProps(filterConfig, "period"),
        title: t("common.entity.table.settlement.column.period", "Period"),
        accessor: "activityPeriodType",
        width: 100,
        sortable: true,
        render: (record) => (
          <Group spacing={4} position="left" noWrap>
            <Anchor component={Link} to={`profile?id=${record.id}`}>
              <FormattedDatabaseCode value={record.activityPeriodType}/>
            </Anchor>
          </Group>
        )
      },
      {
        title: t("common.entity.table.settlement.column.date", "Date"),
        accessor: "fromDttm",
        width: 100,
        sortable: true,
        render: ({activityPeriodType, activityPeriodFromDttm, activityPeriodToDttm}) => (
          <FormattedActivityPeriodDate
            reportTimeType={activityPeriodType}
            fromDate={activityPeriodFromDttm}
            toDate={activityPeriodToDttm}/>
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "name"),
        title: t("common.entity.table.settlement.column.name", "Name"),
        accessor: "name",
        width: 100,
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "company"),
        title: t("common.entity.table.settlement.column.company", "Company"),
        accessor: "company",
        width: 100,
        sortable: true,
        render: ({activityPeriodCompanyName}) => {
          return <DefaultText value={activityPeriodCompanyName}/>
        },
      },
      {
        ...getColumnFilterProps(filterConfig, "companyUnit"),
        title: t("common.entity.table.settlement.column.companyunit", "Unit"),
        accessor: "companyUnit",
        width: 100,
        sortable: true,
        render: ({activityPeriodCompanyUnitId, activityPeriodCompanyUnitName}) => {
          return activityPeriodCompanyUnitId ? <DefaultText value={activityPeriodCompanyUnitName}/> : undefined
        },
      },
      {
        ...getColumnFilterProps(filterConfig, "status"),
        title: t("common.entity.table.settlement.column.status", "Status"),
        accessor: "status.code",
        width: 100,
        sortable: true,
        render: ({status}) => (
          <FormattedDatabaseCode value={status}/>
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "category"),
        title: t("common.entity.table.settlement.column.category", "Category"),
        accessor: "method.code",
        width: 100,
        sortable: true,
        render: ({activityPeriodCategory}) => (
          <FormattedDatabaseCode value={activityPeriodCategory}/>
        ),
      },
      {
        title: '',
        accessor: 'actions',
        width: 50,
        render: (record) => {
          return (
            <Group spacing={4} position="right" noWrap>

            </Group>
          )
        }
      }
    ]
  }, [filterConfig, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  );
};
